import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Search, Phone, Mail, MapPin, Building2, User, Briefcase, Filter, X } from 'lucide-react';

const BDMContactsPage = () => {
  const backendUrl = window.location.hostname === 'localhost' 
    ? 'http://localhost:8001' 
    : (process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001');
  
  const [contacts, setContacts] = useState([]);
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [stats, setStats] = useState({ residential: 0, commercial: 0, smsf: 0, total: 0 });
  const [activeTab, setActiveTab] = useState('residential');
  const [searchTerm, setSearchTerm] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  // States for filtering
  const states = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'NT', 'ACT', 'National'];

  useEffect(() => {
    fetchStats();
    fetchContacts();
  }, []);

  useEffect(() => {
    filterContacts();
  }, [contacts, activeTab, searchTerm, stateFilter]);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/bdm-contacts/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${backendUrl}/api/bdm-contacts`);
      if (response.ok) {
        const data = await response.json();
        setContacts(data);
      }
    } catch (error) {
      console.error('Error fetching contacts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filterContacts = () => {
    let filtered = contacts.filter(contact => contact.category === activeTab);
    
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(contact => 
        (contact.lender_name && contact.lender_name.toLowerCase().includes(search)) ||
        (contact.contact_name && contact.contact_name.toLowerCase().includes(search)) ||
        (contact.email_address && contact.email_address.toLowerCase().includes(search)) ||
        (contact.role_title && contact.role_title.toLowerCase().includes(search))
      );
    }

    if (stateFilter) {
      filtered = filtered.filter(contact => 
        contact.state_region && contact.state_region.includes(stateFilter)
      );
    }

    setFilteredContacts(filtered);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStateFilter('');
  };

  const tabs = [
    { id: 'residential', label: 'Residential Lending', count: stats.residential },
    { id: 'commercial', label: 'Commercial Lending', count: stats.commercial },
    { id: 'smsf', label: 'SMSF Lending', count: stats.smsf }
  ];

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
            BDM Contacts Directory
          </h1>
          <p className="text-slate-500 text-sm sm:text-base mt-2 max-w-2xl">
            Find the right Business Development Manager for your lending needs
          </p>
        </div>

        {/* Stats Cards — pastel tile style matching HomePage */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          <div className="rounded-2xl p-5 border border-slate-200" style={{ background: '#DDEDFC' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Residential</p>
                <p className="text-3xl font-black text-slate-900 tabular-nums">{stats.residential}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-slate-700" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-5 border border-slate-200" style={{ background: '#DCF5E8' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Commercial</p>
                <p className="text-3xl font-black text-slate-900 tabular-nums">{stats.commercial}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-slate-700" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-5 border border-slate-200" style={{ background: '#E9E3FB' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">SMSF</p>
                <p className="text-3xl font-black text-slate-900 tabular-nums">{stats.smsf}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-700" />
              </div>
            </div>
          </div>

          <div className="rounded-2xl p-5 border border-slate-200" style={{ background: '#FFE9DC' }}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">Total Contacts</p>
                <p className="text-3xl font-black text-slate-900 tabular-nums">{stats.total}</p>
              </div>
              <div className="w-10 h-10 rounded-full bg-white/70 flex items-center justify-center">
                <User className="w-5 h-5 text-slate-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-2.5 rounded-full font-semibold text-sm transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-slate-900 text-white shadow-md'
                  : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
              }`}
            >
              {tab.label}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-[11px] font-bold ${
                activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
              }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>

        {/* Search and Filters */}
        <Card className="mb-6 border-2">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
                <Input
                  type="text"
                  placeholder="Search by lender, name, email, or role..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10 h-12 border-2 focus:border-orange-500"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                )}
              </div>

              {/* Filter Toggle Button */}
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant="outline"
                className="h-12 border-2 hover:border-orange-500"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filters
              </Button>

              {/* Clear Filters Button */}
              {(searchTerm || stateFilter) && (
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  className="h-12 border-2 border-red-200 text-red-600 hover:bg-red-50"
                >
                  <X className="w-5 h-5 mr-2" />
                  Clear
                </Button>
              )}
            </div>

            {/* Expandable Filters */}
            {showFilters && (
              <div className="mt-4 pt-4 border-t">
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Filter by State/Region
                </label>
                <div className="flex flex-wrap gap-2">
                  {states.map(state => (
                    <button
                      key={state}
                      onClick={() => setStateFilter(stateFilter === state ? '' : state)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                        stateFilter === state
                          ? 'bg-orange-500 text-white shadow-md'
                          : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                      }`}
                    >
                      {state}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-slate-600">
            Showing <span className="font-bold text-orange-600">{filteredContacts.length}</span> contact{filteredContacts.length !== 1 ? 's' : ''}
            {searchTerm && ` matching "${searchTerm}"`}
            {stateFilter && ` in ${stateFilter}`}
          </p>
        </div>

        {/* Contacts Grid */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
            <p className="mt-4 text-slate-600">Loading contacts...</p>
          </div>
        ) : filteredContacts.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="p-12 text-center">
              <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-lg font-semibold text-slate-700 mb-2">No contacts found</p>
              <p className="text-slate-500">Try adjusting your search or filters</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContacts.map((contact) => (
              <Card key={contact.id} className="border-2 hover:border-orange-300 transition-all duration-300 hover:shadow-xl group">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg font-bold text-slate-800 group-hover:text-orange-600 transition-colors">
                        {contact.lender_name}
                      </CardTitle>
                      {contact.contact_name && (
                        <p className="text-sm font-semibold text-slate-700 mt-1 flex items-center gap-2">
                          <User className="w-4 h-4 text-orange-500" />
                          {contact.contact_name}
                        </p>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {contact.role_title && (
                    <div className="mb-3 px-3 py-2 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600 font-medium">{contact.role_title}</p>
                    </div>
                  )}

                  <div className="space-y-2">
                    {contact.mobile_number && (
                      <a
                        href={`tel:${contact.mobile_number}`}
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-orange-600 transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{contact.mobile_number}</span>
                      </a>
                    )}
                    
                    {contact.phone_number && (
                      <a
                        href={`tel:${contact.phone_number}`}
                        className="flex items-center gap-2 text-sm text-slate-700 hover:text-orange-600 transition-colors"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{contact.phone_number}</span>
                      </a>
                    )}

                    {contact.email_address && (
                      <a
                        href={`mailto:${contact.email_address}`}
                        className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800 transition-colors break-all"
                      >
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="break-all">{contact.email_address}</span>
                      </a>
                    )}

                    {contact.state_region && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        <span>{contact.state_region}</span>
                      </div>
                    )}

                    {contact.other_details && (
                      <div className="mt-3 pt-3 border-t">
                        <p className="text-xs text-slate-500">{contact.other_details}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default BDMContactsPage;
